/**
 * @file DeviceChannelManager.ts
 * @description 设备会话内的通道管理器。
 *
 * 业务 Feature 只按 usageType 申请通道或临时设备，不直接处理 usagePage/usage 查找细节。
 */

import type { ChannelDescriptor } from '../../channels/Channel'
import type { ChannelSelector } from '../../channels/ChannelRole'
import type { HidChannel } from '../../channels/hid/HidChannel'
import type { NormalizedDeviceManifest } from '../../manifests/ManifestTypes'
import { resolveChannelDescriptor } from '../../channels/ChannelResolver'
import type { BaseDevice } from '../BaseDevice'
import {
  createDeviceForChannelDescriptor,
  createHidChannelForDescriptor,
  type OpenChannelOptions,
} from './DeviceChannelOpener'

/**
 * 管理单个设备会话内的控制、固件、屏幕上传等 HID 通道。
 */
export class DeviceChannelManager {
  private readonly openedChannels = new Map<string, HidChannel>()
  private readonly openedDevices = new Set<BaseDevice>()
  private readonly primaryChannel: HidChannel

  constructor(
    private readonly owner: BaseDevice,
    private readonly manifest: NormalizedDeviceManifest,
    primaryChannel: HidChannel,
  ) {
    this.primaryChannel = primaryChannel
    this.register({ usageType: 'default' }, primaryChannel)
  }

  /**
   * 注册运行期已打开的 HID 通道。
   */
  public register(selector: ChannelSelector, channel: HidChannel): void {
    this.openedChannels.set(this.createSelectorKey(selector), channel)

    const descriptor = this.resolve(selector)
    if (descriptor) {
      this.openedChannels.set(this.createSelectorKey(descriptor), channel)
    }
  }

  /**
   * 获取已打开通道，不会触发设备授权或打开动作。
   */
  public get(selector: ChannelSelector = { usageType: 'default' }): HidChannel | undefined {
    const direct = this.openedChannels.get(this.createSelectorKey(selector))
    if (direct) return direct

    const descriptor = this.resolve(selector)
    if (!descriptor) return undefined
    return this.openedChannels.get(this.createSelectorKey(descriptor))
  }

  /**
   * 根据语义查找 manifest 中声明的通道描述。
   */
  public resolve(selector: ChannelSelector): ChannelDescriptor | undefined {
    return resolveChannelDescriptor(this.manifest.channels, selector)
  }

  /**
   * 打开一个辅助 HID 通道。
   *
   * 如果 manifest 未显式声明该通道，但调用方提供了 usagePage/usage，则按调用方给定的
   * HID interface 创建临时描述。这个分支用于底座屏幕上传这类旧 manifest 能力字段。
   */
  public async openChannel(
    selector: ChannelSelector,
    options?: OpenChannelOptions,
  ): Promise<HidChannel | null> {
    const existing = this.get(selector)
    if (existing?.isConnected) return existing
    if (existing) this.unregisterChannel(existing)

    const descriptor = this.resolveOrCreateDescriptor(selector)
    const channel = await createHidChannelForDescriptor(this.owner, descriptor, options)
    if (channel) {
      this.register(this.toSelector(descriptor), channel)
    }
    return channel
  }

  /**
   * 打开一个辅助设备实例。
   *
   * 固件升级需要复用设备驱动的 sendAndWait/事件能力，因此这里返回 BaseDevice。
   */
  public async openDevice(
    selector: ChannelSelector,
    options?: OpenChannelOptions,
  ): Promise<BaseDevice | null> {
    const existing = this.get(selector)
    if (existing && !existing.isConnected) {
      this.unregisterChannel(existing)
    }

    const descriptor = this.resolveOrCreateDescriptor(selector)
    const device = await createDeviceForChannelDescriptor(this.owner, descriptor, options)
    if (device) {
      this.openedDevices.add(device)
      this.register(this.toSelector(descriptor), device.channel)
    }
    return device
  }

  /**
   * 释放通过 openDevice 创建的临时设备。
   *
   * 固件升级设备属于短生命周期资源，用完后既要关闭设备，也要从当前 session 缓存中注销。
   */
  public async releaseDevice(device?: BaseDevice | null): Promise<void> {
    if (!device) return

    try {
      await device.close()
    } finally {
      this.openedDevices.delete(device)
      this.unregisterChannel(device.channel)
    }
  }

  /**
   * 关闭并注销指定语义通道。
   */
  public async close(selector: ChannelSelector): Promise<void> {
    const channel = this.get(selector)
    if (!channel || channel === this.primaryChannel) return
    await this.closeChannel(channel)
  }

  /**
   * 关闭当前 session 管理的所有辅助通道。
   *
   * 默认不关闭主通道，主通道仍由 BaseDevice.close() 负责。
   */
  public async closeAll(options: { includePrimary?: boolean } = {}): Promise<void> {
    for (const device of Array.from(this.openedDevices)) {
      await this.releaseDevice(device)
    }

    const channels = new Set(this.openedChannels.values())
    for (const channel of channels) {
      if (!options.includePrimary && channel === this.primaryChannel) continue
      await this.closeChannel(channel)
    }
  }

  /**
   * 仅注销指定通道，不触发 close。
   */
  public unregister(selector: ChannelSelector): void {
    const channel = this.get(selector)
    if (channel) this.unregisterChannel(channel)
  }

  private resolveOrCreateDescriptor(selector: ChannelSelector): ChannelDescriptor {
    const descriptor = this.resolve(selector)
    if (descriptor) return descriptor

    if (typeof selector.usagePage === 'number' && typeof selector.usage === 'number') {
      return {
        kind: 'hid',
        usageType: selector.usageType,
        usagePage: selector.usagePage,
        usage: selector.usage,
      }
    }

    return {
      kind: 'hid',
      usageType: selector.usageType,
    }
  }

  private toSelector(descriptor: ChannelDescriptor): ChannelSelector {
    return {
      usageType: descriptor.usageType,
      usagePage: descriptor.usagePage,
      usage: descriptor.usage,
    }
  }

  private createSelectorKey(selector: ChannelSelector): string {
    return [selector.usageType ?? '', selector.usagePage ?? '', selector.usage ?? ''].join(':')
  }

  private async closeChannel(channel: HidChannel): Promise<void> {
    try {
      if (channel.isConnected) {
        await channel.close()
      }
    } catch (error) {
      // 关闭辅助通道失败不应阻止主设备关闭；记录后继续清理缓存。
      console.warn('[DeviceChannelManager] Failed to close channel', error)
    } finally {
      this.unregisterChannel(channel)
    }
  }

  private unregisterChannel(channel: HidChannel): void {
    for (const [key, openedChannel] of this.openedChannels.entries()) {
      if (openedChannel === channel) {
        this.openedChannels.delete(key)
      }
    }
  }
}
